import { Suspense } from 'react';
import { NavLink, Outlet } from 'react-router';

import { useAuth } from '@/auth/AuthProvider.js';
import { useCurrentWorkspace } from '@/hooks/use-current-workspace.js';
import { useRecoverSession } from '@/hooks/use-recover-session.js';
import { useSession } from '@/hooks/use-session.js';
import { cn } from '@/lib/cn.js';
import { Button } from '@/components/ui/button.js';
import { Onboarding } from './Onboarding.js';
import { SignedOut } from './SignedOut.js';

const NAV = [
  { to: '/chat', label: 'Chat' },
  { to: '/employees', label: 'Employees' },
  { to: '/integrations', label: 'Integrations' },
  { to: '/approvals', label: 'Approvals' },
];

/**
 * The frame every authenticated screen renders inside - WEB-005.
 *
 * Four states, and each is a real one rather than a spinner standing in for
 * three of them:
 *
 *  * no session - the sign-in screen;
 *  * a session whose `GET /v1/me` is in flight;
 *  * a valid token whose subject has no `app_user` row - `403
 *    USER_NOT_PROVISIONED`, which AUTH-010 exists to resolve and which is
 *    therefore an onboarding screen, not an error;
 *  * signed in and provisioned - the app.
 */
export function AppShell() {
  const { status, signOut } = useAuth();
  const { query, needsOnboarding, isUnauthenticated } = useSession();
  const workspace = useCurrentWorkspace();

  // The access token is fifteen minutes, and since COW-483 there is a refresh
  // behind it. This used to be an inline effect watching this one query, which
  // left every other screen dead-ending on a lapsed token - see
  // `use-recover-session.ts` for what a review measured.
  useRecoverSession({ isUnauthenticated, erroredAt: query.errorUpdatedAt });

  // COW-483: a reload has no tokens but may still have the session cookie, so
  // the provider asks `/v1/session/refresh` before anybody is shown a sign-in
  // screen. Falling through to `SignedOut` here would flash it for the length
  // of that request and then replace it.
  if (status === 'restoring' || status === 'signingOut') {
    return (
      <p role="status" className="p-8 text-sm">
        {status === 'signingOut' ? 'Signing you out…' : 'Restoring your session…'}
        {/* Both are bounded by the client's own request timeout rather than
            running forever - COW-488 - but neither says how long it has been
            waiting, and a person cannot tell a slow answer from a dead one. The
            session query below distinguishes them with `failureCount`; these two
            have no retries to count, so there is nothing honest to show yet. */}
      </p>
    );
  }

  if (status !== 'signedIn') return <SignedOut />;
  if (needsOnboarding) return <Onboarding />;

  if (query.isPending) {
    // `failureCount` distinguishes the two waits that used to look identical:
    // a first attempt in flight, and a retry after something already went
    // wrong. A slow cold start and a dying request rendered the same word, so
    // nobody could tell how long to keep waiting - COW-488's fourth criterion.
    return (
      <p role="status" className="p-8 text-sm">
        {query.failureCount === 0
          ? 'Loading your workspace…'
          : `Loading your workspace… (attempt ${query.failureCount + 1})`}
      </p>
    );
  }

  if (query.isError) {
    // `role="alert"` to match every other error surface in the app -
    // Employees.tsx and all three in Chat.tsx. It was a bare `<section>`, so a
    // test written in the house idiom would look for an alert and not find one,
    // and a screen reader was told nothing had happened.
    return (
      <section role="alert" className="mx-auto max-w-md p-8">
        <h1 className="text-lg font-semibold">We could not load your account</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {query.error instanceof Error ? query.error.message : 'Unknown error'}
        </p>
        <Button className="mt-4" onClick={() => void query.refetch()}>
          Try again
        </Button>
      </section>
    );
  }

  const { user } = query.data;

  return (
    <div className="min-h-screen">
      <header className="flex items-center gap-8 border-b border-border px-6 py-4">
        <span className="font-semibold tracking-tight">Coworker</span>
        <nav aria-label="Main" className="flex gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'rounded-[--radius] px-3 py-1.5 text-sm transition-colors',
                  isActive
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          {/* Fed by the same `/v1/me` response, which is why the shell needs no
              second round trip. WEB-006 makes this a switcher; today it names
              the workspace so a person can see which one they are in - the
              first *active* one, because `workspaces[0]` could be an
              unaccepted invitation whose every request answers 403. */}
          <span aria-label="Workspace" className="text-sm text-muted-foreground">
            {workspace?.name ?? 'No workspace'}
          </span>
          <span className="text-sm">{user.displayName}</span>
          <Button variant="ghost" size="sm" onClick={signOut}>
            Sign out
          </Button>
        </div>
      </header>
      <main className="px-6 py-10">
        {/* Screens become lazy as they gain weight; the boundary belongs here
            from the start so adding one later is not also a layout change. */}
        <Suspense
          fallback={
            <p role="status" className="text-muted-foreground">
              Loading…
            </p>
          }
        >
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}
